import React, { useRef, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Guacamole from "guacamole-common-js";
import CONFIG from "../config";

function Client() {
  const navigate = useNavigate();
  const clientRef = useRef(null);
  const displayRef = useRef(null);
  // const resizeObserverRef = useRef(null);
  const timeoutsRef = useRef(new Set());
  const resizeObserverRef = useRef([]);

  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalError, setModalError] = useState("");
  const [connectionQuality, setConnectionQuality] = useState("good");

  // Extract URL parameters
  const hashParams = new URLSearchParams(window.location.hash.slice(1));
  const token = hashParams.get('token');
  const hostname = hashParams.get("hostname");
  const username = hashParams.get("username");
  const password = hashParams.get("password");
  const protocol = hashParams.get("protocol");

  // Utility function for managing timeouts
  const addTimeout = useCallback((callback, delay) => {
    const timeoutId = setTimeout(() => {
      timeoutsRef.current.delete(timeoutId);
      callback();
    }, delay);
    timeoutsRef.current.add(timeoutId);
    return timeoutId;
  }, []);

  // Clean up all timeouts
  const clearAllTimeouts = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current.clear();
  }, []);

  // Get display dimensions with validation
  const getDisplayDimensions = useCallback(() => {
    const width = window.innerWidth || document.documentElement.clientWidth;
    const height = window.innerHeight || document.documentElement.clientHeight;

    // console.log(`Display dimensions: ${width}x${height}, DPR: ${window.devicePixelRatio || 1}`);
    return { width, height };
  }, []);

  // Enhanced canvas scaling with error handling
  const updateCanvasScaling = useCallback((canvas, rdpWidth, rdpHeight) => {
    const container = displayRef.current;
    if (!container || !canvas || !rdpWidth || !rdpHeight) {
      console.warn('Invalid parameters for canvas scaling');
      return { scaleX: 1, scaleY: 1 };
    }
    try {
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      if (containerWidth <= 0 || containerHeight <= 0) {
        console.warn('Invalid container dimensions');
        return { scaleX: 1, scaleY: 1 };
      }
      const scaleX = containerWidth / rdpWidth;
      const scaleY = containerHeight / rdpHeight;
      // Apply sizing directly (no transform)
      canvas.style.transform = ''; // Clear any previous transform
      canvas.style.transformOrigin = '';
      // canvas.style.width = `${containerWidth}px`;
      // canvas.style.height = `${containerHeight}px`;
      // console.log(`Canvas scaling - Container: ${containerWidth}x${containerHeight}, RDP: ${rdpWidth}x${rdpHeight}, Scale: ${scaleX.toFixed(3)}x${scaleY.toFixed(3)}`);
      return { scaleX, scaleY };
    } catch (error) {
      console.error('Error updating canvas scaling:', error);
      return { scaleX: 1, scaleY: 1 };
    }
  }, []);

  // Token generation with improved error handling
  const generateToken = useCallback(async () => {
    if (!hostname || !username || !password || !protocol) {
      setModalError("Missing connection parameters");
      return false;
    }

    setConnecting(true);
    setShowModal(true);
    setModalError("");

    try {
      const controller = new AbortController();
      const timeoutId = addTimeout(() => controller.abort(), CONFIG.CONNECTION_TIMEOUT_MS);

      const response = await fetch(`${CONFIG.API_BASE_URL}/api/generate-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hostname, username, password, protocol }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      timeoutsRef.current.delete(timeoutId);

      if (!response.ok) {
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.token) {
        throw new Error("No token returned from server");
      }

      navigate(`/client#token=${encodeURIComponent(data.token)}`, { replace: true });
      return true;
    } catch (err) {
      console.error('Token generation error:', err);
      const errorMessage = err.name === 'AbortError'
        ? "Connection timeout - please try again"
        : err.message || "Failed to generate token";

      setModalError(errorMessage);
      addTimeout(() => {
        setShowModal(false);
        navigate("/connect");
      }, CONFIG.ERROR_REDIRECT_DELAY_MS);
      return false;
    } finally {
      setConnecting(false);
    }
  }, [hostname, username, password, protocol, navigate, addTimeout]);

  // Setup mouse handling with proper cleanup
  const setupMouseHandling = useCallback((guacClient, currentScaling) => {
    const display = guacClient.getDisplay();
    const displayElement = display.getElement();

    const mouse = new Guacamole.Mouse(displayElement);

    const handleMouseEvent = (e) => {
      if (!currentScaling.scaleX || !currentScaling.scaleY) return;

      try {
        const rdpX = Math.round(e.state.x / currentScaling.scaleX);
        const rdpY = Math.round(e.state.y / currentScaling.scaleY);

        const scaledState = {
          x: rdpX,
          y: rdpY,
          left: e.state.left,
          middle: e.state.middle,
          right: e.state.right,
          up: e.state.up,
          down: e.state.down
        };

        display.showCursor(true);
        guacClient.sendMouseState(scaledState, true);
        e.stopPropagation();
        e.preventDefault();
      } catch (error) {
        console.error('Mouse event error:', error);
      }
    };

    mouse.onEach(["mousedown", "mouseup", "mousemove"], handleMouseEvent);
    return mouse;
  }, []);

  // Handle URL parameters and token generation
  useEffect(() => {
    if (token) return;

    if (hostname && username && password && protocol) {
      generateToken();
      return;
    }

    navigate("/connect");
  }, [token, hostname, username, password, protocol, navigate, generateToken]);

  // Body class management for fullscreen mode
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;

    if (connected) {
      body.classList.add('connected');
      html.style.overflow = 'hidden';
    } else {
      body.classList.remove('connected');
      html.style.overflow = 'auto';
    }

    return () => {
      body.classList.remove('connected');
      html.style.overflow = 'auto';
    };
  }, [connected]);

  // Main Guacamole client setup
  useEffect(() => {
    if (!token) return;

    const guacClient = new Guacamole.Client(
      new Guacamole.WebSocketTunnel(`${CONFIG.WS_TUNNEL_URL}/guacamole/tunnel`)
    );
    clientRef.current = guacClient;

    let currentScaling = { scaleX: 1, scaleY: 1 };
    let mouse = null;
    let keyboard = null;

    // Connection state handling
    const handleStateChange = (state) => {
      // console.log("Guacamole connection state:", Guacamole.Client.State[state]);

      switch (state) {
        case Guacamole.Client.State.CONNECTED:
          setConnected(true);
          setConnecting(false);
          setConnectionQuality("good");
          addTimeout(() => {
            setShowModal(false);
            // Setup mouse after modal is hidden
            mouse = setupMouseHandling(guacClient, currentScaling);
          }, CONFIG.MODAL_AUTO_HIDE_MS);
          break;

        case Guacamole.Client.State.DISCONNECTED:
        case Guacamole.Client.State.DISCONNECTING:
          setConnected(false);
          setConnectionQuality("poor");
          break;
      }
    };

    // Error handling
    const handleError = (error) => {
      console.error("Guacamole client error:", error);
      setModalError(error.message || "Connection failed or was closed.");
      setShowModal(true);
      setConnected(false);
      setConnecting(false);
    };

    guacClient.onstatechange = handleStateChange;
    guacClient.onerror = handleError;

    // Display handling
    const display = guacClient.getDisplay();

    // Handle display size changes
    display.onresize = (width, height) => {
      // console.log(`RDP display resized to: ${width}x${height}`);
      addTimeout(() => {
        const canvases = displayRef.current?.querySelectorAll('canvas');
        canvases.forEach((canvas) => {
          currentScaling = updateCanvasScaling(canvas, width, height);
        });
      }, 1000);
    };

    // Enhanced appendChild override
    // Enhanced appendChild override
    const originalAppend = display.appendChild;
    display.appendChild = function (element) {
      const result = originalAppend.call(this, element);
      if (element.tagName === 'CANVAS') {
        const canvas = element;
        // Set canvas styles
        Object.assign(canvas.style, {
          position: 'absolute',
          top: '0',
          left: '0',
          imageRendering: 'auto'
        });
        const updateScaling = () => {
          if (canvas.width && canvas.height) {
            currentScaling = updateCanvasScaling(canvas, canvas.width, canvas.height);
          }
        };
        // Set up per-canvas resize observer
        const observer = new ResizeObserver(updateScaling);
        observer.observe(canvas);
        // Store for cleanup (change resizeObserverRef to useRef([]))
        resizeObserverRef.current.push(observer); // Assume resizeObserverRef = useRef([]);
        addTimeout(updateScaling, 100);
      }
      return result;
    };

    // Add display to container
    if (displayRef.current) {
      displayRef.current.appendChild(display.getElement());
    }

    // Debounced resize handler
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (!guacClient) return;
        const { width, height } = getDisplayDimensions();
        guacClient.sendSize(width, height);
        addTimeout(() => {
          const canvases = displayRef.current?.querySelectorAll('canvas');
          canvases.forEach((canvas) => {
            if (canvas && canvas.width && canvas.height) {
              // console.log(`Updating canvas scaling after resize - Canvas: ${canvas.width}x${canvas.height}, Viewport: ${width}x${height}`);
              currentScaling = updateCanvasScaling(canvas, canvas.width, canvas.height);
            }
          });
        }, 1000);
      }, CONFIG.RESIZE_DEBOUNCE_MS);
    };

    window.addEventListener('resize', handleResize);

    // Keyboard handling
    keyboard = new Guacamole.Keyboard(document);
    keyboard.onkeydown = (keysym) => {
      try {
        guacClient.sendKeyEvent(1, keysym);
      } catch (error) {
        console.error('Keyboard event error:', error);
      }
    };
    keyboard.onkeyup = (keysym) => {
      try {
        guacClient.sendKeyEvent(0, keysym);
      } catch (error) {
        console.error('Keyboard event error:', error);
      }
    };

    // Clipboard handling
    guacClient.onclipboard = (stream, mimetype) => {
      if (/^text\//.test(mimetype)) {
        const reader = new Guacamole.StringReader(stream);
        let text = "";
        reader.ontext = (chunk) => (text += chunk);
        reader.onend = () => {
          navigator.clipboard.writeText(text).catch(console.error);
        };
      }
    };

    // File transfer handling
    guacClient.onfile = (stream, mimetype, filename) => {
      try {
        stream.sendAck("Ready", Guacamole.Status.Code.SUCCESS);
        const reader = new Guacamole.BlobReader(stream, mimetype);
        reader.onend = () => {
          const url = URL.createObjectURL(reader.getBlob());
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        };
      } catch (error) {
        console.error('File transfer error:', error);
      }
    };

    // Clipboard paste handler
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === "V") {
        e.preventDefault();
        navigator.clipboard
          .readText()
          .then((text) => {
            if (!text) return;
            try {
              const stream = guacClient.createClipboardStream("text/plain");
              const writer = new Guacamole.StringWriter(stream);
              writer.sendText(text);
              writer.sendEnd();
            } catch (error) {
              console.error('Clipboard paste error:', error);
            }
          })
          .catch(console.error);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // Auto-paste clipboard content on window focus
    const handleWindowFocus = () => {
      if (!guacClient) return;

      navigator.clipboard
        .readText()
        .then((text) => {
          if (text && text.trim()) {
            try {
              const stream = guacClient.createClipboardStream("text/plain");
              const writer = new Guacamole.StringWriter(stream);
              writer.sendText(text);
              writer.sendEnd();
              // console.log('Auto-pasted clipboard content on focus');
            } catch (error) {
              console.error('Auto-paste error:', error);
            }
          }
        })
        .catch((err) => {
          // Silently fail if clipboard access is denied
          console.warn("Clipboard access failed on focus:", err);
        });
    };

    window.addEventListener("focus", handleWindowFocus);

    // Connect with proper dimensions
    const connect = () => {
      setShowModal(true);
      const { width, height } = getDisplayDimensions();
      // console.log(`Connecting with dimensions: ${width}x${height}`);

      try {
        guacClient.connect(`token=${encodeURIComponent(token)}&width=${width}&height=${height}`);
      } catch (error) {
        console.error('Connection error:', error);
        setModalError("Failed to establish connection");
        setShowModal(true);
      }
    };

    connect();

    // Cleanup function
    return () => {
      console.log('Cleaning up Guacamole client...');

      clearAllTimeouts();
      window.removeEventListener('resize', handleResize);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("focus", handleWindowFocus);
      clearTimeout(resizeTimeout);

      if (resizeObserverRef.current) {
        resizeObserverRef.current.forEach(observer => observer.disconnect());
        resizeObserverRef.current = [];
      }

      if (mouse) {
        // Mouse cleanup if available
        try {
          mouse.onEach(["mousedown", "mouseup", "mousemove"], () => { });
        } catch (error) {
          console.warn('Mouse cleanup error:', error);
        }
      }

      if (keyboard) {
        keyboard.onkeydown = null;
        keyboard.onkeyup = null;
      }

      try {
        guacClient.disconnect();
      } catch (error) {
        console.warn('Disconnect error:', error);
      }

      setConnected(false);

      if (displayRef.current) {
        displayRef.current.innerHTML = '';
      }
    };
  }, [token, getDisplayDimensions, updateCanvasScaling, setupMouseHandling, addTimeout, clearAllTimeouts]);

  const handleDisconnect = useCallback(() => {
    if (clientRef.current) {
      try {
        clientRef.current.disconnect();
      } catch (error) {
        console.warn('Disconnect error:', error);
      }
    }
    setConnected(false);
    navigate("/connect");
  }, [navigate]);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setModalError("");
    if (modalError) {
      navigate("/connect");
    }
  }, [modalError, navigate]);

  if (!token && connecting) {
    return (
      <div className="container">
        <div className="loading-spinner">
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div id="display" ref={displayRef}></div>
      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={modalError ? handleCloseModal : undefined}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{modalError ? "Connection Error" : "Connecting..."}</h2>
            <p>
              {modalError
                ? modalError
                : "Please wait, establishing secure connection..."}
            </p>
            {modalError && (
              <button onClick={handleCloseModal} autoFocus>
                Close
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Client;